/**
 * GanttTabsBar — refined-quiet таб-бар проекта (Задачи / Гант / Риски /
 * История). Тонкая обёртка над shadcn `TabsList` + `TabsTrigger`, которая
 * применяет наш CSS-класс `.tab-bar` (см. `project-header.css`). Сам Radix
 * проставляет на активном триггере `data-state="active"` — CSS-правило
 * подчёркивает его 2px-чёрной линией поверх 1px rule-line снизу.
 *
 * Компонент полу-универсальный: набор табов и i18n-namespace задаются props
 * (по дефолту — список табов проекта и `projects.tabs`). Это пригодится
 * backlog 59, где идея табов переиспользуется на других экранах.
 *
 * State табов держит родитель (URL search-param `tab` в `ProjectDetailPage`),
 * Tabs-провайдер тоже остаётся снаружи — здесь только список триггеров.
 */
import { useTranslation } from "react-i18next";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import "./project-header.css";

interface Props {
  /** Идентификаторы табов в порядке отображения. */
  tabs: readonly string[];
  /** i18n-префикс, к которому добавляется `.{tab}` для получения подписи. */
  i18nNamespace?: string;
  /** data-testid для контейнера. */
  "data-testid"?: string;
  /** Префикс data-testid для каждого триггера: `<prefix>-<tab>`. */
  triggerTestIdPrefix?: string;
  /**
   * Опциональные суффиксы (обычно — счётчики), рендерятся справа от лейбла
   * приглушённым `tabular-nums`. Back-compat: undefined → ничего не выводим,
   * существующие вызовы не меняются.
   */
  suffixes?: Record<string, React.ReactNode>;
}

export function GanttTabsBar({
  tabs,
  i18nNamespace = "projects.tabs",
  "data-testid": dataTestId,
  triggerTestIdPrefix,
  suffixes,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  return (
    <TabsList
      variant="line"
      data-testid={dataTestId}
      className="tab-bar h-auto w-auto justify-start gap-0 border-0 bg-transparent p-0"
    >
      {tabs.map((key) => {
        const suffix = suffixes?.[key];
        return (
          <TabsTrigger
            key={key}
            value={key}
            data-testid={triggerTestIdPrefix ? `${triggerTestIdPrefix}-${key}` : undefined}
          >
            {t(`${i18nNamespace}.${key}`)}
            {suffix !== undefined && suffix !== null && (
              <span className="text-muted-foreground ml-1.5 tabular-nums">{suffix}</span>
            )}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}
